import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { map, Observable } from 'rxjs';

import { SignInCredentials } from 'src/app/core/models/SignInCredentials';
import { endpoints } from 'src/environments/endpoints';
import { RecoveryPassRequest } from '../../models/RecoveryPassRequest';
import { SignInResponse } from '../../models/SignInResponse';
import { User } from '../../models/User';
import { UserProfile } from '../../models/UserProfile';
import { IAppState } from '../../store/interfaces/IAppState';
import { selectIsLogged } from '../../store/selectors';

interface UserApiResponse extends User {}

type SignInApiResponse = Omit<SignInResponse, 'user' | 'userProfile'> & {
  user: UserApiResponse;
  userProfile?: UserProfile;
  UserProfile?: UserProfile;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLogged = false;

  constructor(private http: HttpClient, private store: Store<IAppState>) {
    this.setIsLogged();
  }

  signIn(payload: SignInCredentials): Observable<SignInResponse> {
    const baseURL = endpoints.auth;
    const url = `${baseURL}/login`;

    return this.http.post<SignInApiResponse>(url, payload).pipe(
      map((response) => ({
        ...response,
        user: this.normalizeUser(response.user),
        userProfile: response.userProfile
          ? this.normalizeUserProfile(response.userProfile)
          : response.UserProfile
          ? this.normalizeUserProfile(response.UserProfile)
          : undefined,
      }))
    );
  }

  sendRecoveryPasswordEmail(cgc: string): Observable<void> {
    const baseURL = endpoints.auth;
    const url = `${baseURL}/sendRecoveryPasswordEmail`;

    return this.http.post<void>(url, { cgc });
  }

  validateRecoveryPassToken(token: string): Observable<void> {
    const baseURL = endpoints.auth;
    const url = `${baseURL}/validateRecoveryPassToken`;

    return this.http.post<void>(url, { token });
  }

  recoveryPassword(payload: RecoveryPassRequest): Observable<void> {
    const baseURL = endpoints.auth;
    const url = `${baseURL}/recoveryPassword`;

    return this.http.post<void>(url, payload);
  }

  confirmEmail(token: string): Observable<void> {
    const baseURL = endpoints.auth;
    const url = `${baseURL}/activateUser`;

    return this.http.post<void>(url, { token });
  }

  resendEmailConfirmation(cgc: string): Observable<string> {
    const baseURL = endpoints.users;
    const url = `${baseURL}/resendEmailConfirmation`;

    return this.http.post<{ email: string }>(url, { cgc }).pipe(map((response) => response.email));
  }

  getUserByToken(token: string): Observable<User> {
    const baseURL = endpoints.auth;
    const url = `${baseURL}/get-user-by-token`;

    return this.http.post<UserApiResponse>(url, { token }).pipe(map((user) => this.normalizeUser(user)));
  }

  private normalizeUser(user: UserApiResponse): User {
    return user;
  }

  private normalizeUserProfile(userProfile: UserProfile): UserProfile {
    return {
      id: userProfile.id,
      userId: userProfile.userId,
      nome: userProfile.nome,
      email: userProfile.email,
      menu_inicio: userProfile.menu_inicio,
      menu_apolices: userProfile.menu_apolices,
      menu_cadastros: userProfile.menu_cadastros,
      menu_importacao: userProfile.menu_importacao,
      menu_fila_integracao: userProfile.menu_fila_integracao,
      menu_faturamento: userProfile.menu_faturamento,
      menu_rateio: userProfile.menu_rateio,
      menu_extrato: userProfile.menu_extrato,
      menu_chamados: userProfile.menu_chamados,
      menu_pgtos_e_debitos: userProfile.menu_pgtos_e_debitos,
      menu_segunda_via: userProfile.menu_segunda_via,
      menu_downloads: userProfile.menu_downloads,
      menu_faq: userProfile.menu_faq,
      menu_apresentacoes: userProfile.menu_apresentacoes,
      menu_conciliacao: userProfile.menu_conciliacao,
      menu_configuracoes: userProfile.menu_configuracoes,
    };
  }

  validateAppKey(appKey: string, idClient: string): Observable<{ Success: boolean }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Skip-Auth-Redirect': 'true' // impede redirecionamento global em 401
    });

    const baseURL = endpoints.auth;
    const url = `${baseURL}/validateAppKey`;
    const payload ={
      AppKey: appKey,
      IdClient: idClient
    }
    return this.http.post<{ Success: boolean }>(url, payload, { headers });
  }

  private setIsLogged(): void {
    this.store.select(selectIsLogged).subscribe((isLogged) => {
      this.isLogged = isLogged;
    });
  }
}
